import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Autocomplete, CircularProgress, MenuItem, TextField, } from '@mui/material';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { ApiException, useApi } from '@/hooks/useApi';
import useDebouncedFn from '@/hooks/useDebounce';
import { useAuthStore } from '@/store';
import '@/styles/components/OwnerSelect.css';
import usePermissions from '@/hooks/usePermissions';
const getFieldOwnerId = (value) => {
    if (!value)
        return null;
    if (typeof value === 'object' && 'id' in value)
        return value.id;
    const n = Number(value);
    return isNaN(n) || n === 0 ? null : n;
};
const OwnerSelect = ({ children, field, fieldState, formState: _, searchParams, ...inputProps }) => {
    const ownerIds = useAuthStore(s => s.user_metadata?.owner_ids) ?? [];
    const { callApi } = useApi();
    const { showError } = useSnackbar();
    const { isKasmAdmin } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState(new Map());
    const endpoint = useCallback((input) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', '1');
        params.set('page_size', '10');
        if (input)
            params.set('search_filter', input);
        return `/owners?${params.toString()}`;
    }, [searchParams]);
    const fetchOwners = useCallback(async (input) => {
        try {
            setLoading(true);
            const response = await callApi('GET', endpoint(input));
            if (response) {
                const owners = Object.assign([], response.owners);
                setOptions(prev => {
                    const newMap = new Map(prev);
                    owners.forEach(o => newMap.set(o.id, o));
                    return newMap;
                });
            }
        }
        catch (err) {
            const detail = err instanceof ApiException && err.detail;
            showError(detail || 'owners.messages.loadError');
        }
        finally {
            setLoading(false);
        }
    }, [callApi, endpoint, showError]);
    const debouncedFetch = useDebouncedFn(fetchOwners);
    useEffect(() => {
        const loadInitialOwner = async () => {
            if (options.size === 0) {
                const ownerId = getFieldOwnerId(field.value);
                if (ownerId) {
                    try {
                        setLoading(true);
                        const owner = await callApi('GET', `/owners/${ownerId}`);
                        if (owner) {
                            setOptions(new Map([[owner.id, owner]]));
                            field.onChange(owner);
                        }
                    }
                    catch (err) {
                        console.warn(`Failed to fetch owner ${ownerId}, falling back to list`, err);
                        fetchOwners();
                    }
                    finally {
                        setLoading(false);
                    }
                }
                else {
                    fetchOwners();
                }
            }
        };
        loadInitialOwner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const getHubspotId = useCallback((ownerId) => {
        return options.get(ownerId)?.hubspotId ?? '';
    }, [options]);
    const errorMessage = useMemo(() => {
        return fieldState?.error?.message ?? '';
    }, [fieldState?.error?.message]);
    // Auto-select single owner when options are loaded
    useEffect(() => {
        const ownerId = getFieldOwnerId(field.value);
        if (ownerIds.length === 1 && !ownerId && options.size === 1) {
            const [id] = options.keys();
            field.onChange(options.get(id) ?? null);
        }
    }, [ownerIds.length, field, options]);
    const hasOwnerObject = field.value !== null && typeof field.value === 'object';
    if (ownerIds.length === 1 && hasOwnerObject) {
        const owner = field.value;
        return (_jsx(TextField, { ...inputProps, name: field.name, value: owner?.name ?? '', disabled: true, label: children, error: !!errorMessage, helperText: errorMessage || (isKasmAdmin && owner?.hubspotId), size: "small", slotProps: {
                input: {
                    readOnly: true,
                    endAdornment: loading && _jsx(CircularProgress, { size: 20 }),
                },
            } }));
    }
    return (_jsx(Autocomplete, { ...field, value: getFieldOwnerId(field.value), className: "owner-select-form-control", "data-testid": "owner-select-form-control", onChange: (_, value) => field.onChange(value !== null ? (options.get(value) ?? null) : null), open: open, onOpen: () => {
            setOpen(true);
            debouncedFetch();
        }, onClose: () => setOpen(false), onInputChange: (_, input) => debouncedFetch(input), isOptionEqualToValue: (option, value) => Number(option) === Number(value), getOptionLabel: option => options.get(option)?.name ?? '', options: [...options.keys()], loading: loading, renderOption: ({ key, ...props }, id) => (_jsxs(MenuItem, { ...props, className: "owner-select-option", "data-testid": "owner-select-option", dense: true, children: [options.get(id)?.name, isKasmAdmin && _jsx("small", { children: getHubspotId(id) })] }, key)), renderInput: params => (_jsx(TextField, { ...params, ...inputProps, className: "owner-select-text-field", "data-testid": "owner-select-text-field", error: !!errorMessage, helperText: errorMessage ||
                (typeof field.value === 'object'
                    ? (field.value?.hubspotId ?? '')
                    : ''), label: children, size: "small", slotProps: {
                // @ts-expect-error data-testid is valid
                formHelperText: { ['data-testid']: 'owner-select-helper-text' },
                input: {
                    ...params.InputProps,
                    endAdornment: (_jsxs(_Fragment, { children: [loading && _jsx(CircularProgress, { size: 20 }), params.InputProps.endAdornment] })),
                },
            }, sx: {
                '.MuiFormHelperText-root': { marginBottom: '-24px !important' },
            } })) }));
};
export default OwnerSelect;
